import AddIcon from "@mui/icons-material/AddRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { logError } from "@entrilia/shared/logging";
import { parseJsonObject } from "@entrilia/shared/utilities/objectHelper";
import type { AgentInfo, CopilotAgentMessage, MessageUsage } from "../../../../api/copilotAgentTypes";
import { api } from "../../../../api/operationsApi";
import AgentIcon from "../AgentIcon";
import { getAgentIcon } from "../agentIcons";
import { formatEffortLabel } from "../copilotAgentsHelpers";
import MessageBubble from "./MessageBubble";
import SkillsSection from "./SkillsSection";
import ToolsSection from "./ToolsSection";

// Shown when a resumed session was already over the transcript limit. The live "just hit the limit"
// wording arrives from the server as a history event instead.
const truncatedSessionWarning =
  "This session reached the transcript size limit — new messages are not saved to history.";

interface CopilotAgentChatProps {
  agent: AgentInfo;
  sessionId?: string;
  isLoadingModels: boolean;
  model: string;
  models: { id: string; label: string }[];
  onModelChange: (model: string) => void;
  effort: string;
  availableEfforts: string[];
  onEffortChange: (effort: string) => void;
  onNewSession: () => Promise<void>;
  onOpenHistory: () => void;
  initialMessages?: CopilotAgentMessage[];
  initialUsedSkills?: string[];
  initialTranscriptTruncated?: boolean;
  autoSendMessage?: string;
  draft: string;
  onDraftChange: (draft: string) => void;
}

interface EventContent {
  inputTokens: number;
  cachedInputTokens?: number;
  outputTokens: number;
}

const requestNotificationPermission = () => {
  if ("Notification" in window && Notification.permission === "default") {
    Notification.requestPermission().catch((err) => logError(err, "[CopilotAgentChat] requestNotificationPermission"));
  }
};

const notifyIfUserAway = (title: string, options: NotificationOptions) => {
  const isUserActive = !document.hidden && document.hasFocus();
  if (isUserActive || !("Notification" in window) || Notification.permission !== "granted") {
    return;
  }
  const notification = new Notification(title, options);
  notification.onclick = () => {
    window.focus();
    notification.close();
  };
};

const CopilotAgentChat = ({
  agent,
  sessionId,
  isLoadingModels,
  model,
  models,
  onModelChange,
  effort,
  availableEfforts,
  onEffortChange,
  onNewSession,
  onOpenHistory,
  initialMessages,
  initialUsedSkills,
  initialTranscriptTruncated,
  autoSendMessage,
  draft,
  onDraftChange,
}: CopilotAgentChatProps) => {
  const [messages, setMessages] = useState<CopilotAgentMessage[]>(initialMessages ?? []);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [usedSkills, setUsedSkills] = useState<string[]>(initialUsedSkills ?? []);
  const [historyWarning, setHistoryWarning] = useState<string | undefined>(
    initialTranscriptTruncated ? truncatedSessionWarning : undefined
  );
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const autoSentRef = useRef(false);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, statusMessage]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const appendMessage = useCallback((role: "user" | "assistant", content: string) => {
    const msg: CopilotAgentMessage = { id: crypto.randomUUID(), role, content, timestamp: new Date() };
    setMessages((prev) => [...prev, msg]);
    return msg.id;
  }, []);

  const appendDelta = useCallback((delta: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return [...prev.slice(0, -1), { ...last, content: last.content + delta }];
      }
      const msg: CopilotAgentMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: delta,
        timestamp: new Date(),
      };
      return [...prev, msg];
    });
  }, []);

  const appendReasoningDelta = useCallback((delta: string) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return [...prev.slice(0, -1), { ...last, reasoning: (last.reasoning ?? "") + delta }];
      }
      const msg: CopilotAgentMessage = {
        id: crypto.randomUUID(),
        role: "assistant",
        content: "",
        reasoning: delta,
        timestamp: new Date(),
      };
      return [...prev, msg];
    });
  }, []);

  const setMessageUsage = useCallback((usage: MessageUsage) => {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last?.role === "assistant") {
        return [...prev.slice(0, -1), { ...last, usage }];
      }
      return prev;
    });
  }, []);

  const sendMessage = useCallback(
    async (text: string): Promise<boolean> => {
      if (!text || isLoading || !sessionId) {
        return false;
      }

      requestNotificationPermission();
      appendMessage("user", text);
      setIsLoading(true);
      setStatusMessage("Thinking…");
      abortControllerRef.current = new AbortController();

      let failed = false;
      try {
        const startTime = Date.now();
        const stream = api.copilotAgents.sendMessageStream(sessionId, text, abortControllerRef.current.signal);
        for await (const event of stream) {
          switch (event.type) {
            case "delta":
              appendDelta(event.content);
              setStatusMessage("");
              break;
            case "reasoning":
              appendReasoningDelta(event.content);
              break;
            case "status":
              setStatusMessage(event.content);
              break;
            case "error":
              failed = true;
              appendMessage("assistant", `Error: ${event.content}`);
              break;
            case "skill":
              setUsedSkills((prev) => (prev.includes(event.content) ? prev : [...prev, event.content]));
              break;
            case "history":
              setHistoryWarning(event.content);
              break;
            case "done": {
              const data = parseJsonObject<EventContent>(event.content);
              if (data) {
                setMessageUsage({
                  inputTokens: data.inputTokens,
                  cachedInputTokens: data.cachedInputTokens,
                  outputTokens: data.outputTokens,
                  durationMs: Date.now() - startTime,
                });
              }
              break;
            }
            case "keepalive":
              break;
          }
        }
      } catch (err) {
        if (axios.isCancel(err) || (err instanceof Error && err.name === "AbortError")) {
          return true;
        }
        logError(err, "[CopilotAgentChat] sendMessage");
        failed = true;
        appendMessage("assistant", "An unexpected error occurred. Please try again.");
      } finally {
        setIsLoading(false);
        setStatusMessage("");
        abortControllerRef.current = null;
      }

      notifyIfUserAway(`${agent.displayName} ${failed ? "stopped with an error" : "replied"}`, {
        body: text,
        icon: getAgentIcon(agent.slug, new Date())?.src,
      });
      return true;
    },
    [isLoading, sessionId, agent, appendMessage, appendDelta, appendReasoningDelta, setMessageUsage]
  );

  const handleSend = useCallback(() => {
    const text = draft.trim();
    if (!text || isLoading || !sessionId) {
      return;
    }
    onDraftChange("");
    sendMessage(text);
  }, [draft, isLoading, sessionId, sendMessage, onDraftChange]);

  useEffect(() => {
    if (!autoSendMessage || !sessionId || autoSentRef.current) {
      return;
    }
    // Latch before awaiting so a re-render cannot double-send, but release it if the send was refused.
    autoSentRef.current = true;
    sendMessage(autoSendMessage).then((started) => {
      autoSentRef.current = started;
    });
  }, [autoSendMessage, sessionId, sendMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStopGeneration = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  return (
    <Stack sx={{ gap: 1, height: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography variant="subtitle1" sx={{ flex: 1 }}>
          {agent.displayName}{" "}
          <Typography component="span" color="textSecondary">
            [{agent.slug}]
          </Typography>
        </Typography>
        <Select
          value={model}
          onChange={(e: { target: { value: string } }) => onModelChange(e.target.value)}
          disabled={isLoading || isLoadingModels}
          sx={{ minWidth: 200 }}
          startAdornment={isLoadingModels ? <CircularProgress size={16} sx={{ mr: 1 }} /> : undefined}
        >
          {models.map((m) => (
            <MenuItem key={m.id} value={m.id}>
              {m.label}
            </MenuItem>
          ))}
        </Select>
        {availableEfforts.length > 0 && (
          <Select
            value={effort}
            onChange={(e: { target: { value: string } }) => onEffortChange(e.target.value)}
            disabled={isLoading || isLoadingModels}
            sx={{ minWidth: 100 }}
          >
            {availableEfforts.map((e) => (
              <MenuItem key={e} value={e}>
                {formatEffortLabel(e)}
              </MenuItem>
            ))}
          </Select>
        )}
        <Button color="primary" variant="outlined" onClick={onNewSession} disabled={isLoading} startIcon={<AddIcon />}>
          New Session
        </Button>
        <Button
          color="primary"
          variant="outlined"
          onClick={onOpenHistory}
          disabled={isLoading}
          startIcon={<HistoryRoundedIcon />}
        >
          History
        </Button>
      </Stack>
      {historyWarning && (
        <Typography variant="caption" color="warning" sx={{ px: 1 }}>
          {historyWarning}
        </Typography>
      )}
      <Stack direction="row" sx={{ gap: 3 }}>
        {agent.tools.length > 0 && <ToolsSection tools={agent.tools} />}
        {usedSkills.length > 0 && <SkillsSection skills={usedSkills} />}
      </Stack>
      <Box sx={{ flex: 1, overflow: "auto", px: 1 }}>
        {messages.length === 0 && (
          <Stack spacing={2} sx={{ alignItems: "center", justifyContent: "center", height: "100%" }}>
            <AgentIcon slug={agent.slug} size={160} />
            <Typography color="textSecondary">{agent.placeholderText}</Typography>
            {agent.suggestedQueries.length > 0 && (
              <Stack
                direction="row"
                useFlexGap
                sx={{ flexWrap: "wrap", justifyContent: "center", gap: 1.5, maxWidth: 600 }}
              >
                {agent.suggestedQueries.map((query) => (
                  <Chip
                    key={query}
                    label={query}
                    variant="outlined"
                    onClick={() => onDraftChange(query)}
                    sx={{ cursor: "pointer" }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} agentSlug={agent.slug} />
        ))}
        {isLoading && statusMessage && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", pl: 1, mb: 1.5, maxWidth: "80%" }}>
            <CircularProgress size={14} color="secondary" />
            <Typography variant="caption" color="textSecondary">
              {statusMessage}
            </Typography>
          </Stack>
        )}
        <Box ref={bottomRef} />
      </Box>
      <OutlinedInput
        fullWidth
        multiline
        maxRows={4}
        placeholder={agent.placeholderText}
        value={draft}
        onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => onDraftChange(e.target.value)}
        onKeyDown={handleKeyDown}
        endAdornment={
          <InputAdornment position="end">
            {isLoading ? (
              <Tooltip title="Stop">
                <IconButton onClick={handleStopGeneration} edge="end" color="error">
                  <StopCircleRoundedIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <IconButton onClick={handleSend} disabled={!draft.trim() || !sessionId} edge="end">
                <SendRoundedIcon />
              </IconButton>
            )}
          </InputAdornment>
        }
      />
    </Stack>
  );
};

export default CopilotAgentChat;
